import clsx from "clsx";
import { useMemo } from "react";

import { Breadcrumbs } from "@features/navigations/breadcrumbs";

import { ButtonWithIcon } from "@shared/ui/button-with-icon";
import { Body } from "@shared/ui/typography";
import {
  ApplyReplacements,
  getTitleWithIconData,
} from "@shared/ui/typography/apply-replacements-to-text";
import { Heading } from "@shared/ui/typography/heading";
import { mod } from "@shared/utils";

import type { PageHeroPrimaryProps } from "../../types";

import s from "./page-hero-primary.module.scss";

export const PageHeroPrimary = ({
  title,
  description,
  textColor = "milk",
  className,
  breadcrumbs,
  heroData,
}: PageHeroPrimaryProps) => {
  const mods = mod(s, {
    color: textColor,
  });

  const titleSetting = useMemo(() => {
    return getTitleWithIconData(title);
  }, [title]);

  return (
    <section className={clsx(s.root, mods, className)}>
      <Breadcrumbs className={s.breadcrumbs} items={breadcrumbs} />
      <div className={s.content}>
        <div className={clsx(s.left, { [s.fillwidth]: !!description })}>
          {titleSetting.text && (
            <Heading
              level={2}
              tag="h1"
              className={s.title}
              aria-label={titleSetting.text}
            >
              <ApplyReplacements
                text={titleSetting.text}
                settings={titleSetting.settings}
              />
            </Heading>
          )}
          {heroData?.subtitle && <Body>{heroData.subtitle}</Body>}
        </div>
        <div className={s.right}>
          {description && (
            <Body size="caption" className={s.description}>
              {description}
            </Body>
          )}
          {heroData?.buttons && (
            <div className={s.buttons}>
              {/* Кнопка без текста недоступна скринридеру и бесполезна: не рендерим */}
              {heroData?.buttons?.left?.ui?.text && (
                <ButtonWithIcon
                  onClick={heroData?.buttons?.left.onClick}
                  href={heroData?.buttons?.left?.link ?? null}
                  iconName={
                    heroData?.buttons?.left?.ui?.pageIcon?.icon?.name || "plus"
                  }
                  iconFillColor={
                    heroData?.buttons?.left?.ui?.pageIcon?.fillColor || "gold"
                  }
                  iconStrokeColor={
                    heroData?.buttons?.left?.ui?.pageIcon?.strokeColor ||
                    "bistre"
                  }
                  iconSvgCode={
                    heroData?.buttons?.left?.ui?.pageIcon?.uicon?.svg_code
                  }
                  variant="secondary-dark"
                >
                  {heroData?.buttons?.left?.ui?.text}
                </ButtonWithIcon>
              )}
              {heroData?.buttons?.right?.ui?.text && (
                <ButtonWithIcon
                  onClick={heroData?.buttons?.right?.onClick}
                  href={heroData?.buttons?.right?.link ?? null}
                  iconName={
                    heroData?.buttons?.right?.ui?.pageIcon?.icon?.name || "plus"
                  }
                  iconFillColor={
                    heroData?.buttons?.right?.ui?.pageIcon?.fillColor || "gold"
                  }
                  iconStrokeColor={
                    heroData?.buttons?.right?.ui?.pageIcon?.strokeColor ||
                    "bistre"
                  }
                  iconSvgCode={
                    heroData?.buttons?.right?.ui?.pageIcon?.uicon?.svg_code
                  }
                  variant="primary-dark"
                >
                  {heroData?.buttons?.right?.ui?.text}
                </ButtonWithIcon>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
